import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Profile = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const paymentMessage = location.state?.paymentMessage;
  const successfulOrders = orders.filter((order) => order.status === 'completed');

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/orders`, { withCredentials: true });
      setOrders(data || []);
    } catch (error) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (!paymentMessage) return;

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, navigate, paymentMessage]);

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />

      <main className="heritage-shell max-w-5xl flex-1">
        {paymentMessage && (
          <div className="mb-6 rounded-sm border border-[#d7c2a2] bg-[rgba(255,251,243,0.8)] px-4 py-3 text-[#4b2e24]">
            {paymentMessage}
          </div>
        )}

        <div className="mb-10">
          <p className="heritage-kicker mb-2">My Account</p>
          <h1 className="heritage-title text-5xl" data-testid="profile-title">
            Profile
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="heritage-card rounded-sm p-6 lg:col-span-1">
            <h2 className="heritage-title mb-6 text-3xl">Account Details</h2>
            <div className="space-y-4 text-[#1A1A1A]">
              <div>
                <p className="text-sm text-[#6a5448]">Name</p>
                <p className="text-lg" data-testid="profile-name">{user?.name || 'Customer'}</p>
              </div>
              <div>
                <p className="text-sm text-[#6a5448]">Email</p>
                <p className="text-lg break-all" data-testid="profile-email">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-[#6a5448]">Role</p>
                <p className="text-lg capitalize" data-testid="profile-role">{user?.role || 'customer'}</p>
              </div>
            </div>
          </section>

          <section className="heritage-card rounded-sm p-6 lg:col-span-2">
            <h2 className="heritage-title mb-6 text-3xl">Recent Orders</h2>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#c79a35]"></div>
              </div>
            ) : successfulOrders.length === 0 ? (
              <p className="text-[#6a5448]" data-testid="profile-no-orders">No orders yet.</p>
            ) : (
              <div className="space-y-4">
                {successfulOrders.map((order) => {
                  const orderIdentifier = order.razorpay_order_id || order.merchant_order_id || order.phonepe_order_id || order.payment_id;
                  return (
                  <div
                    key={orderIdentifier}
                    className="rounded-sm border border-[#dcc9ad] bg-[rgba(255,251,243,0.7)] p-4"
                    data-testid={`profile-order-${orderIdentifier}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <p className="font-medium text-[#1A1A1A]">{orderIdentifier}</p>
                      <p className="text-sm capitalize text-[#8e755d]">{order.status}</p>
                    </div>
                    <p className="text-sm text-[#6a5448]">
                      {new Date(order.created_at).toLocaleString()}
                    </p>
                    <p className="mt-1 text-sm text-[#6a5448]">
                      {order.items?.length || 0} item(s)
                    </p>
                  </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Profile;
